import { Controller } from "@hotwired/stimulus"

import { forceSetValueOnSelect, setValueAndDispatch } from "../helpers"

// Pytanie o zapis zmienionych danych nabywcy na kartę kontrahenta (#34745).
//
// Bazą porównania są dane karty: przy wejściu na formularz przychodzą z serwera, a po wybraniu
// kontrahenta z listy bazą stają się pola formularza tuż po wypełnieniu ich danymi tej karty
// (zdarzenie `update-form`, warunki przestawienia bazy w `resetBaseline`).
// Bez dopasowanej karty nie ma czego chronić — komunikat znika i nie idzie z nim żadna odpowiedź;
// powiązanie dokumentu z kartoteką i założenie brakującej karty działają także przy odmowie.
// To jedyne miejsce, w którym ta reguła jest opisana; wariant bootstrapowy
// (`invoices/_different_client_data`) powtarza samą mechanikę.
//
// Pola adresujemy po id, tak samo jak robi to wariant bootstrapowy — dzięki temu nie trzeba
// oznaczać każdego pola nabywcy osobnym atrybutem, także tych z zagnieżdżonych partiali.
export default class extends Controller {
  static targets = ["box", "checkbox"]
  static values = { card: Object, clientIdField: String, companyFieldPrefix: String }

  connect() {
    this.baselineClientId = this.fieldValue(this.clientIdFieldValue)
    this.check()
  }

  // `update-form` leci na `window` nie tylko po wybraniu kontrahenta z listy — wysyła je też
  // wyszukiwarka firm (GUS/ARES), i to dla nabywcy, sprzedawcy i odbiorców. Bazę porównania wolno
  // przestawić wyłącznie wtedy, gdy pola nabywcy wypełniła KARTA, czyli gdy zmieniło się
  // powiązanie dokumentu z kartoteką; dane z rejestru mają zostać rozbieżnością do zatwierdzenia,
  // inaczej pytanie o zapis na karcie gaśnie po każdym wyszukaniu firmy.
  resetBaseline(event) {
    const clientId = this.fieldValue(this.clientIdFieldValue)

    if (clientId !== this.baselineClientId) {
      this.baselineClientId = clientId
      this.cardValue = this.cardValues(event?.detail?.client)
    }

    this.check()
  }

  // Bazą są dane WYBRANEJ karty, nie pola formularza tuż po jej wstawieniu: autocomplete nie
  // wypełnia wszystkiego (m.in. note, title, register_number, tva_number, email_for_reminders),
  // więc odczyt z DOM-u zapamiętałby w tych polach resztki po poprzednim nabywcy — a stąd bierze
  // je przycisk „uzupełnij danymi z karty". Payload adresuje pola nazwami kolumn kontrahenta,
  // tak samo jak wariant bootstrapowy (`fill_client_data`).
  cardValues(client) {
    if (!client) return this.formValues()

    return Object.fromEntries(
      Object.keys(this.cardValue).map((field) => [field, client[field.replace(/invoice_|buyer_/g, "")]])
    )
  }

  check() {
    if (this.applying) return

    this.showBox(this.clientPresent() && this.differsFromCard())
  }

  // Odpowiedź o zapis danych nabywcy na kartę trafia do params tylko wtedy, gdy pytanie było
  // widoczne: ukryty checkbox wysyła własne `value="0"` (Rails renderuje je jako pole ukryte),
  // czyli odmowę, o którą nikt nie zapytał, a odmowa jest trwała. Bez odpowiedzi o zapisie
  // decydują warunek daty i reguła własnego śladu — tak samo jak dla API (#34745).
  showBox(visible) {
    this.boxTarget.classList.toggle("hidden", !visible)
    document
      .querySelectorAll(`[name="${this.checkboxTarget.name}"]`)
      .forEach((input) => { input.disabled = !visible })
  }

  clientPresent() {
    return parseInt(this.fieldValue(this.clientIdFieldValue), 10) > 0
  }

  // Puste pole na dokumencie nie jest rozbieżnością: karta trzyma wtedy dane, których dokument
  // po prostu nie wypełnia — tak samo liczy to zapis na karcie, pomijający puste wartości.
  differsFromCard() {
    return Object.keys(this.cardValue).some((field) => {
      const value = this.fieldValue(field)
      if (value === "") return false
      // karta bez informacji o typie kontrahenta nie jest rozbieżnością
      if (field === this.companyFieldPrefixValue && this.cardValue[field] === null) return false

      return value !== this.cardText(field)
    })
  }

  // Odwrotny kierunek: użytkownik woli dane z kartoteki niż te wpisane na dokumencie.
  //
  // Dwa przebiegi, bo zależne kontrolery (VIES, MOSS, DES) na zdarzenie z jednego pola czytają
  // pozostałe wprost z DOM-u: zdarzenie wysłane w trakcie podmiany odpaliłoby je na do połowy
  // zmienionym formularzu i wysłało zapytanie ze starym NIP-em. Najpierw więc wszystkie wartości,
  // potem powiadomienia. `applying` wycisza w tym czasie własny handler tego kontrolera.
  applyCard(event) {
    event.preventDefault()

    const fields = Object.keys(this.cardValue)
    this.applying = true
    // finally, bo wyjątek w podmianie wartości zostawiłby flagę na `true` i wyciszył pytanie
    // o kartę aż do przeładowania strony
    try {
      fields.forEach((field) => this.setField(field, this.cardText(field)))
      fields.forEach((field) => this.notifyField(field))
    } finally {
      this.applying = false
    }

    this.check()
  }

  setField(field, value) {
    if (field === this.companyFieldPrefixValue) {
      document.getElementById(`${field}_${value === "true"}`)?.click()
      return
    }

    const elem = document.getElementById(field)
    // kraj z karty może nie występować na liście opcji selecta (buduje ją kraj dokumentu),
    // a samo przypisanie byłoby wtedy cichym no-opem
    if (elem) forceSetValueOnSelect(elem, value)
  }

  notifyField(field) {
    if (field === this.companyFieldPrefixValue) return

    const elem = document.getElementById(field)
    if (elem) setValueAndDispatch(elem, elem.value)
  }

  formValues() {
    return Object.fromEntries(Object.keys(this.cardValue).map((field) => [field, this.fieldValue(field)]))
  }

  cardText(field) {
    const value = this.cardValue[field]

    return value === null || value === undefined ? "" : String(value)
  }

  // Pole nieobecne w tej wersji formularza (np. buyer_title poza Francją) nie jest rozbieżnością —
  // zwracamy pustą wartość, którą differsFromCard pomija.
  fieldValue(field) {
    if (field === this.companyFieldPrefixValue) {
      const radio = document.getElementById(`${field}_true`)

      return radio ? String(radio.checked) : ""
    }

    return String(document.getElementById(field)?.value ?? "")
  }
}
